import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styles from './Cart.module.scss';
import { fetchOrderStatus, payOrder } from '../../../api/orders';
import { PaymentInfo } from '../../types/CartType';
import { useUiString } from '../../../utils/hook/useUiString';
import {
    SquareCard,
    SquareCardStyle,
    SquarePayments,
    SquareWalletButton,
    useSquarePayments,
} from '../../../utils/hook/useSquarePayments';
import { TipSelector } from './TipSelector';
import { ReactComponent as AppleIcon } from '../../../assets/icons/apple.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { LockIcon } from './icons';

// The payment step of the cart drawer.
//
// By the time this renders the order already exists and the kitchen has
// been notified — this screen only moves money. That is why a failure
// here never destroys the basket: the guest can try another card.

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90_000;

// Square renders the card field in its own iframe, which page CSS cannot
// reach, and its defaults assume a light page — on ours the hints came out
// black on black. These mirror the .input rule in Cart.module.scss so the
// card sits in the sheet like every other field.
const CARD_STYLE: SquareCardStyle = {
    '.input-container': {
        borderColor: '#333333',
        borderRadius: '16px',
    },
    '.input-container.is-focus': {
        borderColor: '#ffffff',
    },
    '.input-container.is-error': {
        borderColor: '#ff6b6b',
    },
    '.message-text': {
        color: '#ffffff',
    },
    '.message-icon': {
        color: '#ffffff',
    },
    '.message-text.is-error': {
        color: '#ff6b6b',
    },
    '.message-icon.is-error': {
        color: '#ff6b6b',
    },
    input: {
        backgroundColor: '#292929',
        color: '#ffffff',
    },
    'input::placeholder': {
        color: '#9d9d9d',
    },
};

interface IProps {
    orderId: string;
    payment: PaymentInfo;
    onPaid: () => void;
    /** Closes the drawer only — the unpaid order stays and reopens here. */
    onClose: () => void;
}

function money(cents: number, currency: string): string {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}

function readError(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        if (data?.message) return data.message;
    }
    return error instanceof Error ? error.message : fallback;
}

export const PaymentStep: FC<IProps> = ({ orderId, payment, onPaid, onClose }) => {
    const t = useUiString();
    const { payments, error: sdkError } = useSquarePayments(
        payment.application_id,
        payment.location_id,
        payment.environment
    );

    const [tipCents, setTipCents] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [waiting, setWaiting] = useState(false);

    const cardRef = useRef<SquareCard | null>(null);
    const [cardReady, setCardReady] = useState(false);

    const amountDue = payment.total_cents + tipCents;
    // Kept in a ref as well: an Apple Pay click must reach tokenize()
    // without any awaited work in between, so the handler cannot rely on
    // reading fresh state.
    const amountRef = useRef(amountDue);
    amountRef.current = amountDue;

    // ── Card field ────────────────────────────────────────────────────
    useEffect(() => {
        if (!payments) return;

        let cancelled = false;
        let attached: SquareCard | null = null;

        void (async () => {
            try {
                const card = await payments.card({ style: CARD_STYLE });
                if (cancelled) { void card.destroy(); return; }
                await card.attach('#tlg-card-container');
                if (cancelled) { void card.destroy(); return; }
                attached = card;
                cardRef.current = card;
                setCardReady(true);
            } catch (err) {
                if (!cancelled) setError(readError(err, t('paymentUnavailable')));
            }
        })();

        return () => {
            cancelled = true;
            cardRef.current = null;
            setCardReady(false);
            if (attached) void attached.destroy();
        };
    }, [payments, t]);

    // ── Settle ────────────────────────────────────────────────────────
    const settle = useCallback(
        async (sourceId: string, verificationToken?: string) => {
            const result = await payOrder(orderId, {
                source_id: sourceId,
                verification_token: verificationToken,
                tip_cents: tipCents,
            });

            if (result.payment_status === 'paid') {
                onPaid();
                return;
            }

            // A wallet or bank payment can still be settling. Poll until
            // Square's webhook has reached the dashboard.
            setWaiting(true);
            const deadline = Date.now() + POLL_TIMEOUT_MS;
            while (Date.now() < deadline) {
                await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
                const status = await fetchOrderStatus(orderId);
                if (status.payment_status === 'paid') { onPaid(); return; }
                if (status.payment_status === 'failed') {
                    throw new Error(status.payment_error || t('paymentFailed'));
                }
            }
            setWaiting(false);
            setError(t('awaitingPayment'));
        },
        [orderId, tipCents, onPaid, t]
    );

    const verify = useCallback(
        async (source: SquarePayments, token: string): Promise<string | undefined> => {
            try {
                const result = await source.verifyBuyer(token, {
                    amount: (amountRef.current / 100).toFixed(2),
                    currencyCode: payment.currency,
                    intent: 'CHARGE',
                    billingContact: {},
                    customerInitiated: true,
                    sellerKeyedIn: false,
                });
                return result?.token;
            } catch {
                // Strong-customer-authentication is only mandatory in some
                // regions. Letting the charge proceed lets Square decide.
                return undefined;
            }
        },
        [payment.currency]
    );

    const payWithCard = async () => {
        const card = cardRef.current;
        if (!card || !payments) return;

        setBusy(true);
        setError(null);
        try {
            const result = await card.tokenize();
            if (result.status !== 'OK' || !result.token) {
                throw new Error(result.errors?.[0]?.message || t('paymentFailed'));
            }
            await settle(result.token, await verify(payments, result.token));
        } catch (err) {
            setWaiting(false);
            setError(readError(err, t('paymentFailed')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className={styles.sheetHeader}>
                <span className={styles.headerSlot} />
                <h4 className={styles.sheetTitle}>{t('payment')}</h4>
                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={onClose}
                    aria-label={t('close')}
                >
                    <CloseIcon className={styles.iconSvg} />
                </button>
            </div>

            <div className={styles.scroll}>
                <div className={styles.card}>
                    <div className={styles.summaryRow}>
                        <span>{t('subtotal')}</span>
                        <span>{money(payment.subtotal_cents, payment.currency)}</span>
                    </div>
                    {payment.tax_cents > 0 && (
                        <div className={styles.summaryRow}>
                            <span>{t('tax')}</span>
                            <span>{money(payment.tax_cents, payment.currency)}</span>
                        </div>
                    )}
                    {tipCents > 0 && (
                        <div className={styles.summaryRow}>
                            <span>{t('tip')}</span>
                            <span>{money(tipCents, payment.currency)}</span>
                        </div>
                    )}
                    <div className={styles.summaryTotal}>
                        <span>{t('amountDue')}</span>
                        <span>{money(amountDue, payment.currency)}</span>
                    </div>
                </div>

                <TipSelector
                    subtotalCents={payment.subtotal_cents}
                    tipCents={tipCents}
                    disabled={busy || waiting}
                    onChange={setTipCents}
                />

                <WalletButtons
                    payments={payments}
                    payment={payment}
                    amountDue={amountDue}
                    disabled={busy || waiting}
                    onToken={async (token) => {
                        setBusy(true);
                        setError(null);
                        try {
                            await settle(token, payments ? await verify(payments, token) : undefined);
                        } catch (err) {
                            setWaiting(false);
                            setError(readError(err, t('paymentFailed')));
                        } finally {
                            setBusy(false);
                        }
                    }}
                />

                <div className={styles.field}>
                    <span className={styles.label}>{t('cardDetails')}</span>
                    <div id="tlg-card-container" className={styles.cardContainer} />
                </div>
            </div>

            <div className={styles.sheetFooter}>
                {(error || sdkError) && <p className={styles.error}>{error ?? sdkError}</p>}
                {waiting && <p className={styles.empty}>{t('awaitingPayment')}</p>}
                <p className={styles.secureNote}>
                    <LockIcon />
                    {t('paymentDataDisclaimer')}
                </p>
                <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={() => void payWithCard()}
                    disabled={!cardReady || busy || waiting}
                >
                    {busy || waiting
                        ? t('paying')
                        : `${t('payNow')} · ${money(amountDue, payment.currency)}`}
                </button>
            </div>
        </>
    );
};

// ── Wallets ───────────────────────────────────────────────────────────
// Apple Pay and Google Pay are rebuilt whenever the amount changes: the
// payment request carries a fixed total, and a stale instance would
// charge the pre-tip figure.

interface WalletMetrics {
    width: number;
    height: number;
    radius: number;
}

// pay.js draws a 240×40 button with 4px corners in its static long form.
const GOOGLE_PAY_DEFAULTS: WalletMetrics = { width: 240, height: 40, radius: 4 };

// Square publishes no constant for the rendered Google Pay button, so it
// is measured off the DOM and the Apple Pay button copies those numbers —
// same box, same corners, whatever Square renders. Google's <button> may
// sit inside a wrapper, so the inner element is preferred: a wrapper would
// report the right width but square corners.
function measureGooglePayButton(): WalletMetrics {
    const container = document.getElementById('tlg-google-pay');
    const node = container?.querySelector('button') ?? container?.firstElementChild ?? null;
    if (!(node instanceof HTMLElement)) return GOOGLE_PAY_DEFAULTS;
    const box = node.getBoundingClientRect();
    const radius = parseFloat(getComputedStyle(node).borderRadius);
    return {
        width:  Math.round(box.width) || GOOGLE_PAY_DEFAULTS.width,
        height: Math.round(box.height) || GOOGLE_PAY_DEFAULTS.height,
        radius: Number.isFinite(radius) ? radius : GOOGLE_PAY_DEFAULTS.radius,
    };
}

const WalletButtons: FC<{
    payments: SquarePayments | null;
    payment: PaymentInfo;
    amountDue: number;
    disabled: boolean;
    onToken: (token: string) => Promise<void>;
}> = ({ payments, payment, amountDue, disabled, onToken }) => {
    const t = useUiString();
    const [available, setAvailable] = useState<{ apple: boolean; google: boolean }>({
        apple: false,
        google: false,
    });
    const appleRef = useRef<SquareWalletButton | null>(null);
    const googleRef = useRef<SquareWalletButton | null>(null);
    // Changing the tip tears the wallets down and builds them again. The
    // teardown is asynchronous, so without this the next attach() could
    // race it and leave the Google Pay container with two buttons or none.
    const teardownRef = useRef<Promise<unknown>>(Promise.resolve());
    const [gpayMetrics, setGpayMetrics] = useState<WalletMetrics | null>(null);

    useEffect(() => {
        if (!payments) return;

        let cancelled = false;
        const built: SquareWalletButton[] = [];

        // One request per wallet. Square's docs never state whether a
        // PaymentRequest may be handed to two payment methods, and the
        // failure mode if it may not is silent — the second wallet simply
        // never appears. Building two costs nothing and removes the doubt.
        const buildRequest = () => payments.paymentRequest({
            countryCode: payment.country,
            currencyCode: payment.currency,
            total: { amount: (amountDue / 100).toFixed(2), label: 'Total' },
        });

        void (async () => {
            await teardownRef.current;
            if (cancelled) return;

            // Neither wallet is available everywhere — Apple Pay needs
            // Safari and a registered domain — so each is attempted on its
            // own and simply not rendered when it throws. Being unavailable
            // is the common case, not an error, but the reason is logged:
            // a silently missing button is otherwise undiagnosable.
            try {
                const apple = await payments.applePay(buildRequest());
                if (cancelled) { void apple.destroy(); } else {
                    appleRef.current = apple;
                    built.push(apple);
                    setAvailable((prev) => ({ ...prev, apple: true }));
                }
            } catch (err) {
                console.debug('[square] Apple Pay unavailable:', err);
            }

            try {
                const google = await payments.googlePay(buildRequest());
                if (cancelled) { void google.destroy(); return; }
                await google.attach('#tlg-google-pay');
                googleRef.current = google;
                built.push(google);
                setAvailable((prev) => ({ ...prev, google: true }));
                if (!cancelled) setGpayMetrics(measureGooglePayButton());
            } catch (err) {
                console.debug('[square] Google Pay unavailable:', err);
            }
        })();

        return () => {
            cancelled = true;
            appleRef.current = null;
            googleRef.current = null;
            // A wallet that fails to tear down must not block the next
            // build, so every rejection is absorbed here.
            teardownRef.current = Promise.all(
                built.map((button) => button.destroy().catch(() => undefined))
            );
            setAvailable({ apple: false, google: false });
            setGpayMetrics(null);
        };
    }, [payments, payment.currency, payment.country, amountDue]);

    // Apple requires tokenize() to run in the same tick as the click, so
    // this handler must not await anything before calling it. The button is
    // read from the ref here rather than captured at render time, so a
    // rebuild after a tip change cannot leave a destroyed instance behind.
    const tap = (ref: React.MutableRefObject<SquareWalletButton | null>) =>
        (event: React.MouseEvent) => {
        // Square's own sample does this before tokenizing.
        event.preventDefault();
        const button = ref.current;
        if (!button || disabled) return;
        void (async () => {
            const result = await button.tokenize();
            if (result.status === 'OK' && result.token) await onToken(result.token);
        })();
    };

    const anyWallet = available.apple || available.google;

    return (
        <>
            {/* Sits in the same centered container as Google Pay so the two
                wallets line up. Safari replaces nothing here — the button is
                entirely ours, drawn as a twin of the black Google Pay button
                and sized from the one Square rendered. */}
            {available.apple && (
                <div className={styles.walletButton}>
                    <button
                        type="button"
                        className={styles.applePayButton}
                        disabled={disabled}
                        onClick={tap(appleRef)}
                        style={gpayMetrics ? {
                            width: gpayMetrics.width,
                            height: gpayMetrics.height,
                            borderRadius: gpayMetrics.radius,
                        } : undefined}
                    >
                        <AppleIcon className={styles.appleIcon} />
                        Pay
                    </button>
                </div>
            )}
            {/* Always in the DOM: googlePay().attach() looks this element up
                by id, so gating it on the availability flag that attaching
                sets would mean it could never be found. Hidden until then.
                attach() only renders the button — the click is ours to wire,
                exactly as it is for Apple Pay. */}
            <div
                id="tlg-google-pay"
                className={styles.walletButton}
                hidden={!available.google}
                onClick={tap(googleRef)}
            />
            {anyWallet && <div className={styles.walletDivider}>{t('orPayWith')}</div>}
        </>
    );
};
